import { useLoadingState } from '@common/hooks';
import { hasAvailability } from '@utils/helpers';
import { mapDiscountToDisplayObject } from '@utils/mapDiscountToDisplayObject';

import { useGetSpaFacilities, useGetSpaFacilitiesPage } from '@features/spa';

import { SpaEntityType } from 'shared-types/Spa';

import { CategoryFilterValue } from '@features/spa/hooks/useSpaEntityFilter';
import { UNCATEGORIZED_VALUE } from '@features/spa/utils/buildCategoryOptions';
import { SpaListItem } from 'voyager';

type UseFacilitiesListParams = {
  propertyId: number;
  language: string;
  categoryFilterId: number | null;
  categoryFilterValue: CategoryFilterValue;
  date: string;
  guests?: number;
};

const mapToListItem = (entity: SpaEntityType, fallbackImageUrl?: string): SpaListItem => ({
  id: String(entity.id),
  name: entity.name,
  imageUrl: entity.image?.url || fallbackImageUrl,
  price:
    entity.price?.amount != null
      ? { amount: entity.price.amount, currencyCode: entity.price.currencyCode }
      : undefined,
  discount: mapDiscountToDisplayObject(entity.discount),
  promotionName: entity.discount?.name,
  duration: entity.displayDuration > 0 ? `${entity.displayDuration} min` : undefined,
  minGuests: entity.minimumGuests,
  maxGuests: entity.maximumGuests ?? undefined,
  unavailable: !entity.availability,
});

export function useFacilitiesList({
  propertyId,
  language,
  categoryFilterId,
  categoryFilterValue,
  date,
  guests,
}: UseFacilitiesListParams) {
  const {
    data: facilities = [],
    isFetching: isFetchingFacilities,
    isError,
  } = useGetSpaFacilities(propertyId, language, date, null, 0, true);

  const { data: facilitiesPage } = useGetSpaFacilitiesPage(propertyId, language);
  const fallbackImageUrl = facilitiesPage?.heroBackgroundImage?.url;

  const { isInitialLoading, isRefetching } = useLoadingState(
    isFetchingFacilities,
    facilities.length > 0
  );

  const allEntities = facilities.slice().sort((a, b) => {
    const aAvailable = hasAvailability(a, date);
    const bAvailable = hasAvailability(b, date);
    if (aAvailable !== bAvailable) return aAvailable ? -1 : 1;
    return a.priority - b.priority;
  });

  const categoryFiltered =
    categoryFilterValue === UNCATEGORIZED_VALUE
      ? allEntities.filter(({ categories }) => !categories || categories.length === 0)
      : categoryFilterId
      ? allEntities.filter(({ categories }) =>
          categories.find(
            (category: { id: string | number }) => String(category.id) === String(categoryFilterId)
          )
        )
      : allEntities;

  const filteredEntities = guests
    ? categoryFiltered.filter(
        (e) => (e.minimumGuests ?? 0) <= guests && (e.maximumGuests ?? Infinity) >= guests
      )
    : categoryFiltered;

  const listItems: SpaListItem[] = filteredEntities.map((entity) =>
    mapToListItem(entity, fallbackImageUrl)
  );

  const uncategorizedItems = facilities.filter(
    (item) => !item.categories || item.categories.length === 0
  );

  return {
    listItems,
    uncategorizedItems,
    isError,
    isFetching: isFetchingFacilities,
    isInitialLoading,
    isRefetching,
  };
}
